import * as React from "react";
import { useThree } from "@react-three/fiber";
import { useGpuTier } from "../../hooks/useGpuTier";

export type DprRange = [number, number];

/** On low-density (devicePixelRatio 1) monitors the canvas gets so few pixels
 *  that textured 3D reads soft next to browser-rasterized DOM text; capable
 *  GPUs render supersampled there (floor 1.5, browser downscales).
 *
 *  The cap must stay 2 — never raise it to native density on 3× phones. A
 *  dpr-3 scene costs 2.25× the GPU memory of dpr-2 (canvas + both MSAA FBOs
 *  scale with it), and iOS WebKit reclaims a force-lost context lazily: on an
 *  SPA route change back to a 3D page the old and new scenes briefly coexist
 *  in GPU memory, and two dpr-3 scenes exceed the tab's limit — Safari kills
 *  the page ("A problem repeatedly occurred", iPhone Pro, 2026-07). */
export function dprRangeForTier(tier: number): DprRange {
  return tier >= 2 ? [1.5, 2] : [1, 2];
}

type Props = {
  /** Reports the GPU-resolved dpr range. Feed it back into the host Canvas's
   *  `dpr` prop: R3F's configure() re-applies that prop's clamp (default
   *  [1, 2]) on every Canvas re-render, so a floor raised only through
   *  setDpr() would be silently reset moments later. */
  onRange?: (range: DprRange) => void;
};

/** R3F resolves devicePixelRatio once at Canvas mount and never re-reads it,
 *  so a window dragged onto a display with a different pixel density keeps the
 *  stale backing-store scale — on the denser screen every canvas pixel is
 *  stretched and the whole 3D scene goes soft while DOM text stays crisp.
 *  Resize doesn't help (the old dpr is reused); only a reload would. Re-apply
 *  the clamp on mount and whenever the media resolution changes. */
export function DprSync({ onRange }: Props) {
  const setDpr = useThree((s) => s.setDpr);
  const { tier } = useGpuTier();
  const range = React.useMemo(() => dprRangeForTier(tier), [tier]);

  React.useEffect(() => {
    onRange?.(range);
  }, [onRange, range]);

  React.useEffect(() => {
    setDpr(range);
    if (typeof window.matchMedia !== "function") return undefined;
    let mql: MediaQueryList | null = null;
    let disposed = false;
    const onChange = () => {
      mql?.removeEventListener("change", onChange);
      setDpr(range);
      subscribe();
    };
    // The query matches only the CURRENT ratio, so each change means "ratio
    // is no longer this one" — re-subscribe against the new ratio after every
    // fire to keep catching subsequent monitor moves.
    const subscribe = () => {
      if (disposed) return;
      mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mql.addEventListener("change", onChange);
    };
    subscribe();
    return () => {
      disposed = true;
      mql?.removeEventListener("change", onChange);
    };
  }, [setDpr, range]);
  return null;
}
